import React, { useState, useEffect } from 'react';
import { ChartBarIcon, ArrowDownTrayIcon, ArrowUpIcon, ArrowDownIcon, CalendarIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import CommonDataTable from '../../Common/CommonDataTable';

const StockMovementsReport = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [movementData, setMovementData] = useState([]);
  const [summary, setSummary] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);

  const [filters, setFilters] = useState({
    warehouseId: '',
    itemId: '',
    dateFrom: '',
    dateTo: '',
    movementType: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const [warehousesRes, itemsRes] = await Promise.all([
        inventoryApi.getWarehouses(),
        inventoryApi.getItems({ limit: 1000 })
      ]);
      setWarehouses(warehousesRes.data?.data || warehousesRes.data || []);
      setItems(itemsRes.data?.data || itemsRes.data || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadMovements = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.itemId) params.itemId = filters.itemId;
      if (filters.movementType) params.movementType = filters.movementType;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await inventoryApi.getMovementsReport(params);
      const data = response?.data || response;

      setMovementData(data.movements || []);
      setSummary(data.summary || {});
    } catch (error) {
      console.error('Error loading movements:', error);
      setMovementData([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ warehouseId: '', itemId: '', dateFrom: '', dateTo: '', movementType: '' });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.itemId) params.itemId = filters.itemId;
      if (filters.movementType) params.movementType = filters.movementType;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await inventoryApi.getMovementsReportCSV(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock-movements-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      key: 'transactionDate',
      header: 'Date',
      sortable: true,
      render: (value, row) => (
        <div className="text-sm">
          {new Date(row.transactionDate).toLocaleDateString()}
          <div className="text-xs text-gray-500">{new Date(row.transactionDate).toLocaleTimeString()}</div>
        </div>
      )
    },
    {
      key: 'item',
      header: 'Item',
      render: (value, row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.item?.name}</div>
          <div className="text-sm text-gray-500">SKU: {row.item?.sku}</div>
        </div>
      )
    },
    { key: 'warehouse', header: 'Warehouse', render: (value, row) => row.warehouse?.name },
    {
      key: 'movementType',
      header: 'Type',
      render: (value, row) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
          {row.movementType?.replace('_', ' ').toUpperCase()}
        </span>
      )
    },
    {
      key: 'direction',
      header: 'Direction',
      render: (value, row) => (
        <div className="flex items-center">
          {row.direction === 'in' ? (
            <ArrowUpIcon className="h-4 w-4 text-green-600 mr-1" />
          ) : (
            <ArrowDownIcon className="h-4 w-4 text-red-600 mr-1" />
          )}
          <span className={`font-semibold ${row.direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
            {row.direction?.toUpperCase()}
          </span>
        </div>
      )
    },
    {
      key: 'quantity',
      header: 'Quantity',
      sortable: true,
      render: (value, row) => <div className="text-right font-semibold">{row.quantity} {row.unit}</div>
    },
    {
      key: 'unitCost',
      header: 'Unit Cost',
      sortable: true,
      render: (value, row) => <div className="text-right">Rs {row.unitCost?.toFixed(2) || '0.00'}</div>
    },
    {
      key: 'totalCost',
      header: 'Total Cost',
      sortable: true,
      render: (value, row) => <div className="text-right font-semibold">Rs {row.totalCost?.toFixed(2) || '0.00'}</div>
    },
    {
      key: 'status',
      header: 'Status',
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          row.status === 'completed' ? 'bg-green-100 text-green-800' :
          row.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {row.status?.toUpperCase()}
        </span>
      )
    }
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Filters Card */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-3 flex items-center gap-3 rounded-t-xl">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <ChartBarIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Stock Movement Report</h2>
              <p className="text-indigo-200 text-xs">Full inbound and outbound transaction history</p>
            </div>
          </div>

          <div className="px-5 py-4 flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Warehouse</label>
              <select
                value={filters.warehouseId}
                onChange={(e) => handleFilterChange('warehouseId', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[160px]"
              >
                <option value="">All Warehouses</option>
                {warehouses.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Item</label>
              <select
                value={filters.itemId}
                onChange={(e) => handleFilterChange('itemId', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[180px]"
              >
                <option value="">All Items</option>
                {items.slice(0, 100).map(item => (
                  <option key={item._id} value={item._id}>{item.name} ({item.sku})</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Movement Type</label>
              <select
                value={filters.movementType}
                onChange={(e) => handleFilterChange('movementType', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[160px]"
              >
                <option value="">All Types</option>
                <option value="receipt">Receipt</option>
                <option value="issue">Issue</option>
                <option value="transfer_in">Transfer In</option>
                <option value="transfer_out">Transfer Out</option>
                <option value="adjustment_in">Adjustment In</option>
                <option value="adjustment_out">Adjustment Out</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                <CalendarIcon className="w-3.5 h-3.5 text-indigo-400" />
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[160px]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                <CalendarIcon className="w-3.5 h-3.5 text-indigo-400" />
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[160px]"
              />
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner size="lg" fullScreen={false} />
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div className="flex-grow overflow-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="space-y-4">
            {summary && Object.keys(summary).length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Movements</h3>
                  <p className="text-2xl font-bold text-indigo-600">{summary.totalMovements || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Inbound</h3>
                  <p className="text-2xl font-bold text-green-600">{summary.inboundMovements || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Outbound</h3>
                  <p className="text-2xl font-bold text-red-600">{summary.outboundMovements || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Value</h3>
                  <p className="text-2xl font-bold text-purple-600">Rs {summary.totalValue?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-indigo-600 px-4 py-3 flex items-center justify-between">
                <span className="text-sm font-medium text-white">Stock Movements ({movementData.length})</span>
              </div>
              <CommonDataTable
                data={movementData}
                columns={columns}
                searchable={true}
                searchPlaceholder="Search movements..."
                emptyMessage="No movement data available"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockMovementsReport;
